var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { storeCsrfToken } from "commonConfig";
import { EDUID_CONFIG_URL } from "globals";
import { handleApiError, handleBaseQueryError } from "./helpers/errorHandlers";
import { hasCsrfToken, isApiError, isApiResponse, isErrorResult } from "./helpers/typeGuards";
const ajaxHeaders = {
    "Content-Type": "application/json; charset=utf-8",
    Accept: "application/json",
    "Accept-Encoding": "gzip,deflate",
    "Cache-Control": "no-store, no-cache, must-revalidate",
    Pragma: "no-cache",
    "X-Requested-With": "XMLHttpRequest",
};
export const customBaseQuery = (args, api, extraOptions) => __awaiter(void 0, void 0, void 0, function* () {
    const state = api.getState();
    let csrf_token = state.config.csrf_token;
    const service_urls = {
        jsConfig: EDUID_CONFIG_URL,
        signup: state.config.signup_service_url,
        personalData: state.config.personal_data_service_url,
        authn: state.config.authn_service_url,
        security: state.config.security_service_url,
        orcid: state.config.orcid_service_url,
        email: state.config.emails_service_url,
        letterProofing: state.config.letter_proofing_service_url,
        bankid: state.config.bankid_service_url,
        eidas: state.config.eidas_service_url,
        frejaeID: state.config.freja_eid_service_url,
        ladok: state.config.ladok_service_url,
        login: state.config.login_service_url,
        resetPassword: state.config.reset_password_service_url,
    };
    if (!(extraOptions === null || extraOptions === void 0 ? void 0 : extraOptions.service)) {
        return customError("No service specified");
    }
    if (!(extraOptions.service in service_urls)) {
        return customError("Unknown service: " + extraOptions.service);
    }
    const baseUrl = service_urls[extraOptions.service];
    if (baseUrl === undefined) {
        return customError("No url for service: " + extraOptions.service);
    }
    const rawBaseQuery = createBaseQuery(baseUrl, (args === null || args === void 0 ? void 0 : args.body) === undefined ? "GET" : "POST");
    // Add CSRF token to body if needed
    const base_args = addCsrfTokenToArgs(args, csrf_token);
    // call backend api
    let result = yield rawBaseQuery(base_args, api, extraOptions);
    if (!isErrorResult(result) && isInvalidSessionError(result.data)) {
        // The backend invalidated the session while answering, which also cleared the CSRF token
        // server side. Retrying with the token from this response would fail with "CSRF failed to
        // validate", since that token was minted into the now dead session. Fetch a fresh token from
        // the service root, so this browser is not left holding a token from the dead session, and
        // retry the request itself when there is a body to retry with. A request without a body
        // (a GET) carries no CSRF token, so there is nothing to retry - but the token still has to be
        // refreshed, or the next POST goes out with the dead session's token.
        const fresh_csrf_token = yield fetchFreshCsrfToken(baseUrl, csrf_token, api, extraOptions);
        if (fresh_csrf_token) {
            csrf_token = fresh_csrf_token;
            if ((args === null || args === void 0 ? void 0 : args.body) !== undefined) {
                result = yield rawBaseQuery(replaceCsrfTokenInArgs(args, fresh_csrf_token), api, extraOptions);
            }
        }
    }
    else if (!isErrorResult(result) && isCsrfValidationError(result.data) && (args === null || args === void 0 ? void 0 : args.body) !== undefined) {
        // The opposite situation: the session answering is alive and well, and it is the token this
        // browser sent that is stale. The token in this response is the one that session expects, so
        // store it and retry once. Re-authenticating the user (see handleApiError) would throw away a
        // half finished password reset over a token this browser can simply correct.
        const live_csrf_token = handleCsrfTokenFromResponse(result.data, csrf_token, api);
        if (live_csrf_token && live_csrf_token !== csrf_token) {
            csrf_token = live_csrf_token;
            result = yield rawBaseQuery(replaceCsrfTokenInArgs(args, live_csrf_token), api, extraOptions);
        }
    }
    // manage results of api call
    if (isErrorResult(result)) {
        yield handleBaseQueryError(result, csrf_token, api, state);
    }
    else if (isApiError(result.data)) {
        // The CSRF token is not extracted from error responses in general. The backend mints a token
        // into the session it holds while answering, and for session errors (resetpw.invalid_session)
        // that session has just been invalidated - storing that token poisons every following request
        // with "CSRF failed to validate".
        //
        // A response that failed CSRF validation is the one error response whose token can be trusted:
        // it was produced by a live session that is still serving this browser, and it is the token we
        // sent that is stale. Storing it is what lets handleApiError's re-authentication go out with a
        // token the backend will accept, instead of the same stale one that just failed.
        //
        // The two cases can never be confused: an invalidated session reports through
        // payload.message ("resetpw.invalid_session") and never sets payload.error.csrf_token, while a
        // CSRF validation failure reports through payload.error.csrf_token. Do not "simplify" this
        // back into extracting the token from every error response - that is the bug this guard exists
        // to prevent.
        if (isCsrfValidationError(result.data)) {
            csrf_token = handleCsrfTokenFromResponse(result.data, csrf_token, api);
        }
        return yield handleApiError(result.data, result.meta, csrf_token, api);
    }
    else {
        // extract CSRF token from response
        handleCsrfTokenFromResponse(result.data, csrf_token, api);
    }
    return result;
});
// The backend returns this message when the session held by the browser can not be used for the
// request. The session is cleared server side before answering, so the correct response is to
// retry the request - but only after getting a CSRF token that belongs to the new session.
function isInvalidSessionError(data) {
    return isApiError(data) && data.payload.message === "resetpw.invalid_session";
}
// The backend returns this when the CSRF token sent with the request is not the one the session
// holds. The session itself is alive, so the token in the response can be used to try again.
function isCsrfValidationError(data) {
    var _a, _b;
    return isApiError(data) && ((_b = (_a = data.payload.error) === null || _a === void 0 ? void 0 : _a.csrf_token) === null || _b === void 0 ? void 0 : _b[0]) === "CSRF failed to validate";
}
// Issue a GET to the service root to get a CSRF token for the current session.
function fetchFreshCsrfToken(baseUrl, csrf_token, api, extraOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        const result = yield createBaseQuery(baseUrl, "GET")({ url: "" }, api, extraOptions);
        if (isErrorResult(result) || isApiError(result.data)) {
            return undefined;
        }
        return handleCsrfTokenFromResponse(result.data, csrf_token, api);
    });
}
function addCsrfTokenToArgs(args, csrf_token) {
    if ((args === null || args === void 0 ? void 0 : args.body) !== undefined && args.body.csrf_token === undefined) {
        return Object.assign(Object.assign({}, args), { body: Object.assign(Object.assign({}, args.body), { csrf_token }) });
    }
    return args;
}
// Used for retries, where the token the caller put in the body is the reason the request failed.
// addCsrfTokenToArgs would leave that token in place.
function replaceCsrfTokenInArgs(args, csrf_token) {
    return Object.assign(Object.assign({}, args), { body: Object.assign(Object.assign({}, args.body), { csrf_token }) });
}
function handleCsrfTokenFromResponse(data, csrf_token, api) {
    if (isApiResponse(data) && hasCsrfToken(data)) {
        const new_csrf_token = data.payload.csrf_token;
        if (new_csrf_token && new_csrf_token !== csrf_token) {
            api.dispatch(storeCsrfToken(new_csrf_token));
            delete data.payload.csrf_token;
            return new_csrf_token;
        }
    }
    return csrf_token;
}
function customError(error) {
    return {
        error: {
            status: "CUSTOM_ERROR",
            error: error,
        },
    };
}
function createBaseQuery(baseUrl, method) {
    return fetchBaseQuery({
        baseUrl,
        credentials: "include",
        redirect: "manual",
        method,
        headers: ajaxHeaders,
        responseHandler: "content-type",
    });
}
export const eduIDApi = createApi({
    baseQuery: customBaseQuery,
    reducerPath: "eduIDApi",
    endpoints: () => ({}),
});
